<script lang="ts" setup>
import { useStepper } from "@vueuse/core";
import { z } from "zod";
import {
  reactive,
  ref,
  computed,
  useToast,
  useUser,
  useRouter,
  useAuth,
  useRoute,
} from "#imports";
import MPImg from "~/components/widgets/MPImg.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPMessage from "~/components/widgets/MPMessage.vue";
import MPInput from "~/components/forms/MPInput.vue";

const router = useRouter();
const route = useRoute();

defineExpose({
  closed: () => {
    if (route.path == "/confirm") {
      router.push("/explore");
    }
  },
});

const stepper = useStepper({
  intro: {
    isValid: () => true,
  },
  upload: {
    isValid: () => formSchema.safeParse(formModel).success,
  },
  terms: {
    isValid: () => termsSchema.safeParse(termsModel).success,
  },
  complete: {
    isValid: () => true,
  },
});

const formModel = reactive({
  file: ref<File | null>(null),
  url: ref<string | null>(null),
});

const termsModel = reactive({
  termsAccepted: false,
  copyrightAccepted: false,
  compensationAccepted: false,
});

const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target?.files) {
    formModel.file = target.files[0]!;
  }
};

// const fileSizeLimit = 5000000
const termsSchema = z.object({
  termsAccepted: z.literal(true),
  copyrightAccepted: z.literal(true),
  compensationAccepted: z.literal(true),
});

const formSchema = z
  .object({
    url: z.string().url().startsWith("https://maps.app.goo.gl/").nullish(),
    file: z.instanceof(File).nullish(),
  })
  .refine((data) => data.url != null || data.file != null, {
    message:
      "You need to provide a file or a shared google map link (https://maps.app.goo.gl/1234)",
  });

const isLoading = ref(false);
const { showError } = useToast();
const { user } = useUser();
const authStore = useAuth();

const handleSubmit = async () => {
  isLoading.value = true;
  try {
    await authStore.creatorApplication(formModel);
    stepper.goToNext();
  } catch (e) {
    console.error("Submission error:", e);
    showError(`Could not complete submission`, e);
  } finally {
    isLoading.value = false;
  }
};

const userOnBoarded = computed(
  () =>
    user.terms_audit_log &&
    user.terms_audit_log.length > 0 &&
    user.is_onboarded,
);

const errorMessage = ref<string | null>(null);
const handleStepValidation = (message: string) => {
  errorMessage.value = null;

  const validation = formSchema.safeParse(formModel);
  if (validation.success) {
    stepper.goToNext();
  } else {
    errorMessage.value = message;
  }
};
</script>

<template>
  <div class="creator-onboarding" v-if="user && userOnBoarded">
    <div class="onboarding-step intro-step">
      <h3>You have already been onboarded</h3>
      <p class="subtitle">
        Looks like you have already been completed your onboarding. No further
        action is needed.
      </p>
      <div class="stacked-already-onboarded">
        <MPImg
          src="/media/images/already_onboarded_image.webp"
          class="intro-image-1"
        />
        <MPImg
          src="/media/images/already_onboarded_vector.svg"
          class="intro-image-2"
        />
        <MPImg
          src="/media/images/already_onboarded_bar.webp"
          class="intro-image-5"
        />

        <MPImg
          src="/media/images/already_onboarded_rollercoaster.webp"
          class="intro-image-4"
        />

        <MPImg
          src="/media/images/already_onboarded_coffee.webp"
          class="intro-image-3"
        />
      </div>

      <div class="onboarding-step-actions">
        <MPButton
          backgroundColor="white"
          label="Start Exploring"
          version="secondary"
          size="large"
          @click="router.push('/explore')"
        />
      </div>
    </div>
  </div>
  <div class="creator-onboarding" v-if="user && !userOnBoarded">
    <form @submit.prevent="handleSubmit">
      <div v-if="stepper.isCurrent('intro')" class="onboarding-step intro-step">
        <span class="typeface-summary">Creator Onboarding</span>
        <h3>Let’s discover the world together</h3>
        <p>
          Welcome to Mappr! Create personalized spots recommendation lists and
          earn recurring revenue.
        </p>
        <div class="stacked-images">
          <MPImg
            src="/media/images/creator-onboarding-bg-graphic.png"
            class="intro-image-1"
          />
          <MPImg
            src="/media/images/creator-onboarding-graphic-2.webp"
            class="intro-image-2"
          />
          <MPImg
            src="/media/images/creator-onboarding-graphic-1.webp"
            class="intro-image-3"
          />
        </div>

        <div class="onboarding-step-actions">
          <MPButton
            backgroundColor="white"
            label="Create my map"
            version="secondary"
            size="large"
            @click="stepper.goToNext"
          />
        </div>
      </div>
      <div
        v-if="stepper.isCurrent('upload')"
        class="onboarding-step upload-step"
      >
        <ul
          v-if="!stepper.isCurrent('complete') && !stepper.isCurrent('intro')"
          class="onboarding-dots"
        >
          <li
            v-for="(_step, id) in stepper.steps.value"
            :key="id"
            :class="{ active: !stepper.isCurrent(id) && !stepper.isBefore(id) }"
          />
        </ul>
        <h3>Upload your Map</h3>
        <p>
          Upload your map with at least 30 spots to get started. Feel free to
          add more for better recommendations.
        </p>
        <MPMessage v-if="errorMessage" version="error">
          {{ errorMessage }}
        </MPMessage>
        <div class="onboarding-step-actions">
          <div class="file-upload-wrapper">
            <label for="file">
              <i class="ri-add-circle-line" />
              &nbsp;{{
                formModel.file?.name
                  ? formModel.file.name
                  : "Add PDF, XLSX, XLS or DOC file."
              }}
            </label>
            <input
              id="file"
              type="file"
              name="pdf-file"
              accept=".pdf"
              @change="handleFileUpload"
            />
          </div>
          <hr />
          <small>Use Google Maps list</small>
          <div class="google-maps-link-wrapper">
            <MPImg src="/media/images/google-maps-logo.png" class="maps-logo" />
            <MPInput
              v-model="formModel.url"
              name="url"
              placeholder="Add URL"
              helpText="Password? Remember to have your list unprotected"
            />
          </div>
          <div class="map-button-container">
            <MPButton
              label="Next"
              class="go-to-terms-button"
              version="primary"
              size="large"
              @click="
                handleStepValidation(
                  'You need to provide a file or a shared google map link (https://maps.app.goo.gl/1234)',
                )
              "
            />
            <MPButton
              label="Skip for now"
              version="secondary"
              size="large"
              @click="stepper.goToNext()"
            />
          </div>
        </div>
      </div>
      <div v-if="stepper.isCurrent('terms')" class="onboarding-step terms-step">
        <ul
          v-if="!stepper.isCurrent('complete') && !stepper.isCurrent('intro')"
          class="onboarding-dots"
        >
          <li
            v-for="(step, id) in stepper.steps.value"
            :key="id"
            :class="{ active: !stepper.isCurrent(id) && !stepper.isBefore(id) }"
          />
        </ul>
        <MPMessage v-if="errorMessage" version="error">
          {{ errorMessage }}
        </MPMessage>
        <h3>Accept Terms & Conditions</h3>
        <p>
          Please review and accept the required documents to finalize your setup
          and publish your map.
        </p>
        <div class="onboarding-step-actions">
          <ul class="terms">
            <small>
              <li>
                <label for="terms-checkbox">
                  <input
                    id="terms-checkbox"
                    v-model="termsModel.termsAccepted"
                    type="checkbox"
                    required
                  />
                  I have read and agree to the
                  <a target="_blank" href="https://mappr.com/info/creator-terms"
                    >Terms and Conditions (v1)</a
                  >
                  of MAPPREXPLORE SL.
                </label>
              </li>
              <li>
                <label for="copyright-checkbox">
                  <input
                    id="copyright-checkbox"
                    v-model="termsModel.copyrightAccepted"
                    type="checkbox"
                    required
                  />
                  I acknowledge and accept the
                  <a target="_blank" href="https://mappr.com/info/copyright"
                    >Copyright Policy (v1)</a
                  >
                  of MAPPREXPLORE SL.
                </label>
              </li>
              <li>
                <label for="compensation-terms">
                  <input
                    id="compensation-terms"
                    v-model="termsModel.compensationAccepted"
                    type="checkbox"
                    required
                  />
                  I agree to the
                  <a
                    target="_blank"
                    href="https://mappr.com/info/creator-compensation"
                    >Creator Compensation Terms (v1)</a
                  >
                  provided by MAPPREXPLORE SL.
                </label>
              </li>
            </small>
          </ul>
          <div>
            <MPButton
              label="Back"
              version="secondary"
              size="large"
              @click="stepper.goToPrevious"
            />
            &nbsp;
            <MPButton
              type="submit"
              label="Next"
              class="submit-application-button"
              version="primary"
              size="large"
              :isLoading="isLoading"
            />
          </div>
        </div>
      </div>
      <div
        v-if="stepper.isCurrent('complete')"
        class="onboarding-step complete-step"
      >
        <ul
          v-if="!stepper.isCurrent('complete') && !stepper.isCurrent('intro')"
          class="onboarding-dots"
        >
          <li
            v-for="(step, id) in stepper.steps.value"
            :key="id"
            :class="{ active: !stepper.isCurrent(id) && !stepper.isBefore(id) }"
          />
        </ul>
        <span class="typeface-summary">Congratulations</span>
        <h3>Onboarding Complete</h3>
        <p>
          Your map is under review and being prepared. We’ll email you once it’s
          ready to publish.
        </p>
        <MPImg
          src="/media/images/creator-onboarding-end-graphic.webp"
          class="creator-onboarding-img"
        />
        <NuxtLinkLocale to="/studio" class="onboarding-step-actions">
          <MPButton
            version="info"
            backgroundColor="white"
            size="large"
            label="Go to Dashboard"
          />
        </NuxtLinkLocale>
      </div>
    </form>
  </div>
</template>

<style lang="postcss" scoped>
.stacked-images {
  margin-block: 1rem;
  display: grid;
  place-items: center;

  img {
    max-height: 18rem;
    grid-row: 1/-1;
    grid-column: 1/-1;
  }

  .intro-image-2 {
    margin-left: -8rem;
    margin-top: 4rem;
  }
  .intro-image-3 {
    margin-left: 8rem;
  }
}

.stacked-already-onboarded {
  margin-block: 1rem;
  display: grid;
  place-items: center;

  img {
    max-height: 18rem;
    grid-row: 1/-1;
    grid-column: 1/-1;
  }

  .intro-image-2 {
    margin-left: -5rem;
    margin-top: 2rem;
  }
  .intro-image-3 {
    margin-top: -10rem;
    margin-left: -8rem;
    width: 14rem;
  }
  .intro-image-4 {
    margin-top: -5rem;
    margin-left: -8rem;
    width: 14rem;
  }
  .intro-image-5 {
    margin-top: -1rem;
    margin-left: -8rem;
    width: 14rem;
  }
}

.creator-onboarding {
  max-width: 28rem;
  height: 100%;

  form {
    height: 100%;
  }
}

.onboarding-step {
  height: 100%;
  padding-block: 2rem;
  display: grid;
  align-items: center;
  text-align: center;

  h3 {
    margin: 0.5rem 0;
  }
  p {
    margin: 0.5rem 0;
  }
}

.intro-step {
  color: white;
  background-color: var(--color-rose-400);
  button {
    color: var(--color-rose-400) !important;
  }

  .subtitle {
    padding-inline: 3rem;
  }
}

.upload-step {
  padding-inline: 2rem;
  h3 {
    color: var(--color-rose-400);
  }
  p {
    color: var(--color-slate-400);
  }
}

.terms-step {
  padding-inline: 2rem;
  h3 {
    color: var(--color-rose-400);
  }
  p {
    color: var(--color-slate-400);
  }
}

.complete-step {
  padding-inline: 2rem;
  color: white;
  background-color: var(--color-rose-400);
  button {
    color: var(--color-rose-400) !important;
  }
}

hr {
  width: 100%;
  border-bottom: 1px solid var(--color-slate-200);
  border-image: linear-gradient(
      to right,
      var(--color-slate-200) 0%,
      transparent 50%,
      var(--color-slate-200) 100%
    )
    1;
  border-top: 0;
  margin: 0.75rem 0;
  overflow: visible;
  &:after {
    content: "or";
    display: inline-block;
    position: relative;
    bottom: -0.5rem;
    margin: 0 0.5rem -1rem;
    color: var(--color-slate-400);
  }
}

.onboarding-step-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  button {
    max-width: fit-content;
  }
}

.creator-onboarding-img {
  margin: 0 auto;
  max-width: 15rem;
}

.google-maps-link-wrapper {
  position: relative;
  img {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    width: 1.75rem;
    height: 1.75rem;
    z-index: 10;
  }
  &:deep(input) {
    padding-left: 2.75rem;
  }
}

.file-upload-wrapper {
  position: relative;
  margin: 1rem 0 -0.5rem;
  #file {
    border: 2px solid var(--color-slate-200);
    padding: 4rem 0;
    width: 100%;
    background: var(--color-slate-50);
    border-radius: 0.5rem;
    align-items: center;
    text-align: center;
    color: var(--color-slate-50);
    &::file-selector-button {
      display: none;
    }
  }
  label {
    position: absolute;
    top: 50%;
    width: 100%;
    left: 50%;
    transform: translate(-50%, -25%);
    color: var(--color-slate-400);
  }
}

.onboarding-dots {
  display: flex;
  justify-content: center;
  list-style: none;
  margin: 0;
  padding: 0;
  margin-bottom: 1rem;
  li {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: white;
    border: 2px solid var(--color-slate-200);
    margin: 0 0.5rem;
    &.active {
      background: var(--color-rose-300);
      border: 1px solid var(--color-rose-400);
    }
  }
  li:last-child {
    display: none;
  }
}

.map-button-container {
  display: flex;
  width: 100%;
  justify-content: space-between;

  button {
    width: 100%;
  }
}

.terms {
  list-style: none;
  padding: 0 1rem;
  margin: 0;
  label {
    display: block;
    padding-left: 1rem;
    text-indent: -1.5rem;
  }
  li {
    align-items: left;
    text-align: left;
    margin: 1rem 0;
  }
  a {
    text-decoration: underline;
  }
}
</style>
